import * as React from 'react'
import { Image, Text, TouchableOpacity, View, SafeAreaView } from 'react-native'
import styled from '@theme/styled-components'

import Icon from '@components/icon/Icons'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'
import Drawer from '@components/drawer/Drawer'
import Select from '@components/select/Select'
import FilePicker from '@components/filePicker/FilePicker'
import CameraPicker from '@components/imagePicker/CameraPicker'
import LibraryPicker from '@components/imagePicker/ImagePicker'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import theme from '@theme/Theme'

const HORIZONTAL_PADDING = 24
const MAX_FILES = 2

interface Props {
  onSubmit: (comment: string, images: PieceJointeCommentaireRequest[]) => Promise<void>
  submitStatus: Status
}

const IncidentDetailComponent = ({ submitStatus, onSubmit }: Props) => {
  const i18n = useI18n()

  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const [comment, setComment] = React.useState('')
  const [files, setFiles] = React.useState<PieceJointeCommentaireRequest[]>([])

  const handleSubmit = () => {
    onSubmit(comment, files).then(() => {
      Drawer.close()
      setComment('')
      setFiles([])
    })
  }

  const addFiles = (addedFiles: FileUpload[]) => {
    const uploadFiles = addedFiles.map((af) => ({ filename: af.filename, fileContent: af.data })).slice(0, MAX_FILES)
    setFiles((prevState) => [...prevState, ...uploadFiles])
  }

  const addImages = (addedImages: UploadImageIncidentRequest[]) => {
    const uploadImages = addedImages
      .map((img) => ({ filename: img.filename, fileContent: img.image }))
      .slice(0, MAX_FILES)
    setFiles((prevState) => [...prevState, ...uploadImages])
  }

  const renderFile = (file: PieceJointeCommentaireRequest) => (
    <AttachementContainer key={file.filename}>
      <Attachment source={{ uri: file.fileContent }} />
      <Delete onPress={() => deleteImage(file)}>
        <Icon name="close" size={15} color={theme.colors.background} />
      </Delete>
    </AttachementContainer>
  )

  const deleteImage = (image: PieceJointeCommentaireRequest) => {
    setFiles((prevState) => prevState.filter((p) => p.filename !== image.filename))
  }

  if (isPhone) {
    return (
      <MobileContainer>
        <Title>{i18n.t('screens.incidentDetail.addComment')}</Title>
        <CloseButton activeOpacity={0.9} onPress={Drawer.close}>
          <Icon name="close" size={24} color={theme.colors.primary} />
        </CloseButton>
        <Content>
          <Body>
            <TextInput
              value={comment}
              onChange={setComment}
              numberOfLines={4}
              placeholder={i18n.t('screens.incidentDetail.commentPlaceholder')}
            />
            {files.length < MAX_FILES && (
              <FileContainer>
                <FileInputView>
                  <FilePicker onFileUploaded={addFiles}>
                    <Select
                      disabled
                      label={i18n.t('screens.incidentDetail.import')}
                      onClick={() => undefined}
                      left
                      icon="upload"
                    />
                  </FilePicker>
                </FileInputView>
                <FileInputView>
                  <LibraryPicker onImageUploaded={addImages} limit={MAX_FILES}>
                    <Select
                      disabled
                      label={i18n.t('screens.incidentDetail.importImage')}
                      onClick={() => undefined}
                      left
                      icon="image"
                    />
                  </LibraryPicker>
                </FileInputView>
                <FileInputView>
                  <CameraPicker onImageUploaded={addImages}>
                    <Select
                      disabled
                      label={i18n.t('screens.incidentDetail.takePicture')}
                      onClick={() => undefined}
                      left
                      icon="photo"
                    />
                  </CameraPicker>
                </FileInputView>
              </FileContainer>
            )}

            <ImagesContainer>{files.map(renderFile)}</ImagesContainer>
          </Body>

          <ButtonContainer>
            <Button
              status={!comment && files.length === 0 ? 'disabled' : submitStatus === 'loading' ? 'loading' : 'active'}
              label={i18n.t('common.send')}
              onClick={handleSubmit}
            />
          </ButtonContainer>
        </Content>
      </MobileContainer>
    )
  }

  return (
    <Container>
      <InputContainer>
        <TextInput
          value={comment}
          onChange={setComment}
          right={{
            name: 'send',
            onClick: handleSubmit,
            disabled: (!comment && files.length === 0) || submitStatus === 'loading',
          }}
          placeholder={i18n.t('screens.incidentDetail.commentPlaceholder')}
          error={submitStatus === 'error' ? i18n.t('common.error') : undefined}
        />
        <ImagesContainer>{files.map(renderFile)}</ImagesContainer>
      </InputContainer>

      <FileInputView>
        {files.length < MAX_FILES && (
          <FilePicker onFileUploaded={addFiles}>
            <Icon name="image" size={24} color={theme.colors.primary} />
          </FilePicker>
        )}
      </FileInputView>
    </Container>
  )
}

export default IncidentDetailComponent

const InputContainer = styled(View)`
  flex: 1;
  margin-top: 8px;
`

const Container = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`
const FileContainer = styled(View)`
  margin-top: 16px;
`
const FileInputView = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? `8px 0px` : `${HORIZONTAL_PADDING}px`)};
`

const MobileContainer = styled(SafeAreaView)`
  padding: 0 16px 16px;
  flex: 1;
`

const ButtonContainer = styled(View)`
  padding: 16px;
  background-color: ${(props) => props.theme.colors.background};
`
const Title = styled(Text)`
  text-align: center;
  margin-bottom: 36px;
  z-index: -1;
  ${(props) => props.theme.fonts.Headline};
`
const CloseButton = styled(TouchableOpacity)`
  padding: 16px;
  position: absolute;
  top: -18px;
  left: 0;
  z-index: 10;
`

const ImagesContainer = styled(View)`
  margin-top: 16px;
  flex-flow: row wrap;
`

const Attachment = styled(Image)`
  width: ${(props) => (props.theme.breakpoint === 'phone' ? 75 : 50)}px;
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 75 : 50)}px;
`

const Delete = styled(TouchableOpacity)`
  position: absolute;
  right: 2px;
  top: 2px;
  width: 15px;
  height: 15px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.disable};
  justify-content: center;
  align-items: center;
`

const AttachementContainer = styled(View)`
  padding: 8px;
`

const Content = styled(View)`
  flex: 1;
  justify-content: space-between;
`

const Body = styled(View)`
  padding: 16px;
`
